
import { Vue, Options, prop } from "vue-class-component";
import { PropType } from "vue";
import Checkbox from "primevue/checkbox";
import {
  formatByCountry,
  formatInputByCountry,
  formatPriceRM,
  formatPriceRP,
  parseByCountry
} from "@/app/infrastructures/misc/Utils";
import {
  CreateBookingV4,
  PaymentMethodBookingV4,
  PaymentMethodV4,
  ProtectionPackingBookingV4
} from "@/domain/entities/BookingV4";
// import WoodpackingCheckbox from "@/app/ui/views/shipment/booking/component/woodpacking-checkbox/index.vue";
import { InsuranceTypeDeliveryData } from "@/domain/entities/Booking";
import { ConfigValidationForm } from "@/domain/entities/CreateBookingV3";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import { FlagsPermissionBookingV4 } from "@/feature-flags/flag-booking-v4";
import { ExchangeRateController } from "@/app/ui/controllers/ExchangeRateController";
import { ExchangeRateData } from "@/domain/entities/ExchangeRate";
import { AccountController } from '@/app/ui/controllers/AccountController';

class Props {
  modelValue = prop<CreateBookingV4>({
    default: new CreateBookingV4(),
    type: Object
  });
  protectionPacking = prop<ProtectionPackingBookingV4>({
    required: true,
    type: ProtectionPackingBookingV4
  });
  currencyPrefix = prop<string>({
    required: true,
    type: String
  });
  numericLocale = prop<string>({
    default: "id",
    type: String
  });
  // GENESIS-21348 — destination-currency mode for Harga Barang/Tagihan COD:
  // integer-only (maxDecimal=0) formatting via formatInputMoneyWithDecimal
  // instead of formatInputByCountry (which only knows ID/MY separators and
  // wouldn't format S$/B$ correctly). Parent (create-booking-client.vue)
  // owns the cross-country-route + payment-method gating; this component
  // only needs the resulting boolean.
  isCrossCountryCod = prop<boolean>({ default: false, type: Boolean });
  agentDiscountCommission = prop<string | undefined>({ default: undefined });
  showAgentDiscount = prop<boolean>({ default: true, type: Boolean });
  itemPriceError = prop<string | undefined>({ default: undefined });
  insuranceBasic = prop<InsuranceTypeDeliveryData>({
    default: () => new InsuranceTypeDeliveryData(),
    type: Object
  });
  paymentMethod = prop<PaymentMethodBookingV4>({
    default: () => new PaymentMethodBookingV4(),
    required: true,
    type: PaymentMethodBookingV4
  });
  isShowCODAmountField = prop<boolean>({ default: false, type: Boolean });
  // Adjust v4 rule-based disable (GENESIS-20184 continuation) — default EDITABLE
  configInsurance = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configCodAmount = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configGoodsPrice = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configWoodpacking = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
}

@Options({
  name: "ProtectionPackingSummary",
  emits: ["update:protectionPacking", "blurItemPrice", "update:paymentMethod"],
  components: { Checkbox },
  watch: {
    isCrossCountryCod: {
      handler() {
        this.getExchangeRate();
      }
    }
  }
})
class ProtectionPackingSummary extends Vue.with(Props) {
  get showInsuranceThresholdBanner(): boolean {
    return FlagsPermissionBookingV4.show_banner_insurance_threshold.isEnabled();
  }

  get protectionLabelDisplay(): string {
    return (
      this.protectionPacking.protectionLabel ||
      (this.$t("bookingV4.protection.checkbox") as string)
    );
  }

  get disabledInsurance(): boolean {
    return this.configInsurance.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get hiddenInsurance(): boolean {
    return this.configInsurance.rule.includes(ConfigValidationForm.NON_SHOW);
  }

  get disabledCodAmount(): boolean {
    return this.configCodAmount.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get hiddenCodAmount(): boolean {
    return this.configCodAmount.rule.includes(ConfigValidationForm.NON_SHOW);
  }

  get disabledWoodpacking(): boolean {
    return this.configWoodpacking.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get disabledGoodsPrice(): boolean {
    return this.configGoodsPrice.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get hiddenWoodpacking(): boolean {
    return this.configWoodpacking.rule.includes(ConfigValidationForm.NON_SHOW);
  }

  get itemPriceDisplay() {
    const raw = this.protectionPacking.itemPrice;
    if (raw === null || raw === undefined) return "";
    if (this.isCrossCountryCod) {
      return formatInputByCountry(raw, {
        type: "money",
        country: this.modelValue.receiver.destinationCountry.code
      });
    }
    return formatInputByCountry(raw, {
      type: "money",
      country: this.inputCountryCode
    });
  }

  formatItemPrice(val: string) {
    if (this.isCrossCountryCod) {
      return formatInputByCountry(val, {
        type: "money",
        country: this.modelValue.receiver.destinationCountry.code
      });
    }
    return formatInputByCountry(val, {
      type: "money",
      country: this.inputCountryCode
    });
  }

  get inputCountryCode(): "ID" | "MY" {
    const malaysiaCodes = ["MY", "MYS", "MS-MY"];
    return malaysiaCodes.includes(this.numericLocale.trim().toUpperCase())
      ? "MY"
      : "ID";
  }

  emitProtectionPacking(fields: Partial<ProtectionPackingBookingV4>) {
    if (fields.changeType !== "woodpacking") fields.changeType = "";
    this.$emit(
      "update:protectionPacking",
      new ProtectionPackingBookingV4({
        ...this.protectionPacking,
        ...fields
      })
    );
  }

  onToggleProtection(checked: boolean) {
    if (this.disabledInsurance || this.protectionPacking.protectionDisabled) {
      return;
    }

    this.emitProtectionPacking({
      protectionChecked: checked,
      insuranceType: checked ? "basic" : "free",
      hasChanged: true,
      lastChecked: checked
    });
  }

  onItemPriceInput(value: string) {
    const parsed = this.isCrossCountryCod
      ? parseByCountry(value, {
          country: this.modelValue.receiver.destinationCountry.code
        })
      : parseByCountry(value, {
          country: this.inputCountryCode
        });
    this.emitProtectionPacking({ itemPrice: parsed });
    this.$emit("update:price", parsed);
    if (this.isShowCODAmountField) {
      this.emitPaymentMethod({ codAmount: parsed });
    }
  }

  onItemPriceBlur() {
    this.$emit("blurItemPrice");
  }

  onToggleWoodpacking(checked: boolean) {
    if (this.disabledWoodpacking) return;
    this.emitProtectionPacking({
      woodpacking: checked,
      changeType: "woodpacking"
    });
  }

  toggleTooltip(event: Event) {
    const refs: any = this.$refs;
    refs["opTooltipProteksi"].toggle(event);
  }

  formatPrice(value: number) {
    return this.numericLocale === "my"
      ? formatPriceRM(value)
      : formatPriceRP(value);
  }

  get codAmountDisplay() {
    const raw = this.paymentMethod.codAmount;
    if (raw === null || raw === undefined) return "";
    if (this.isCrossCountryCod) {
      return formatInputByCountry(raw, {
        type: "money",
        country: this.modelValue.receiver.destinationCountry.code
      });
    }
    return formatInputByCountry(raw, {
      type: "money",
      country: this.inputCountryCode
    });
  }

  onCodAmountInput(value: any) {
    if (this.disabledCodAmount) return;
    const formatted = this.isCrossCountryCod
      ? parseByCountry(value, {
          country: this.modelValue.receiver.destinationCountry.code
        })
      : parseByCountry(value, {
          country: this.inputCountryCode
        });
    this.paymentMethod.codAmount = formatted;
  }

  emitPaymentMethod(fields: Partial<PaymentMethodBookingV4>) {
    this.$emit(
      "update:paymentMethod",
      new PaymentMethodBookingV4({
        ...this.paymentMethod,
        ...fields
      })
    );
  }

  onCodAmountBlur() {
    if (this.paymentMethod.codAmount) return;
    const itemPrice = this.protectionPacking.itemPrice;
    this.emitPaymentMethod({ codAmount: itemPrice });
  }

  get estimatedConvertedCodAmount(): string {
    const codAmount = this.paymentMethod.codAmount || 0;

    if (!this.configExchangeRate.id || !codAmount) {
      return "";
    }

    const rate = this.configExchangeRate.exchangeRate;

    const total = codAmount * rate;

    return this.$t("bookingV4.protection.codAmount.footerEstimated", {
      currency: this.modelValue.sender.originCountry.currencySymbol,
      amount: formatByCountry(total, {
        type: "weight",
        country: this.modelValue.sender.originCountryCode,
        fractionDigits: 6,
        minFractionDigits: 0
      })
    }) as string;
  }

  get showCodAmountField(): boolean {
    return (
      this.paymentMethod.method === PaymentMethodV4.COD &&
      this.isShowCODAmountField &&
      !this.hiddenCodAmount
    );
  }

  get isInternalAccount(): boolean {
    return AccountController.accountData.isInternalAccount;
  }

  async getExchangeRate() {
    if (!this.isCrossCountryCod) return;

    await ExchangeRateController._onGetList({
      from: this.modelValue.receiver.destinationCountry.currency,
      to: this.modelValue.sender.originCountry.currency,
      status: "active",
      page: 1,
      limit: 1
    });

    this.configExchangeRate = new ExchangeRateData(
      ExchangeRateController.exchangeRateData.data?.[0]
    );
  }

  configExchangeRate = new ExchangeRateData();
}

export default ProtectionPackingSummary;
